import React from 'react';
import * as _ from 'lodash';
import Action, { IActionParentProps } from '../Action';
import useStreamingSession from '../../hooks/useSession';
import { OggFieldService } from '../../services/ogg.service';
import { Rating } from '../../types';
import { useSettings } from '../../hooks/useSettings';
import {
  ISimpleTableCell,
  Table,
  TableColumnLayout,
  renderSimpleCell,
} from 'azure-devops-ui/Table';
import { ObservableValue } from 'azure-devops-ui/Core/Observable';
import { ArrayItemProvider } from 'azure-devops-ui/Utilities/Provider';
import { ISimpleListCell } from 'azure-devops-ui/List';
import { Button } from 'azure-devops-ui/Button';

interface IRaterContentProps {
  streamingContent: string;
  oggService: OggFieldService;
}

interface ITableItem extends ISimpleTableCell {
  category: ISimpleListCell;
  rating: ISimpleListCell;
}

export function calculateOverallScore(rating: Rating) {
  const criteriaCount = Object.values(rating).filter((x) => !!x).length;
  if (criteriaCount == 0) return 0;

  const sum: number = Object.values(rating).reduce((sum, current) => sum + current, 0);
  return sum / criteriaCount;
}

function RaterContent(props: IRaterContentProps) {
  const settings = useSettings();

  function toRatingResponse(message: string): Rating {
    const obj = JSON.parse(message);
    return _.mapKeys(obj, (value, key) => _.camelCase(key));
  }

  function trySuffixes(suffixes: string[]) {
    for (let suffix of suffixes) {
      try {
        return toRatingResponse(props.streamingContent + suffix);
      } catch (error) {}
    }

    console.debug(
      "Warning: Couldn't create a Rating response after applying suffixes. Here's the input: ",
      `'${props.streamingContent}'`
    );
    return {};
  }

  const rating: Rating = trySuffixes(['', '"}', '""}', '\\""}']);
  console.debug('loading rating');

  const fixedColumns = [
    {
      columnLayout: TableColumnLayout.singleLinePrefix,
      id: 'category',
      name: 'Category',
      readonly: true,
      renderCell: renderSimpleCell,
      width: new ObservableValue(-30),
    },
    {
      id: 'rating',
      name: 'Rating',
      readonly: true,
      renderCell: renderSimpleCell,
      width: new ObservableValue(-30),
    },
  ];

  function displayStars(count?: number) {
    if (!count) return { text: 'N/A' };
    return { text: '⭐'.repeat(count) };
  }

  const tableItems = new ArrayItemProvider<ITableItem>([
    {
      category: { text: 'Detail' },
      rating: displayStars(rating.detail),
    },
    {
      category: { text: 'Business Case' },
      rating: displayStars(rating.businessCase),
    },
    {
      category: { text: 'Reasoning' },
      rating: displayStars(rating.reasoning),
    },
    {
      category: { text: 'Flow' },
      rating: displayStars(rating.flow),
    },
    {
      category: { text: 'Calculations' },
      rating: displayStars(rating.calculations),
    },
    {
      category: { text: 'Personas / User Groups' },
      rating: displayStars(rating.userGroups),
    },
    {
      category: { text: 'Terms' },
      rating: displayStars(rating.terms),
    },
    {
      category: { text: 'Overall Score', textClassName: 'overall' },
      rating: { text: `${calculateOverallScore(rating).toFixed(1)}/5`, textClassName: 'overall' },
    },
  ]);

  return (
    <div className='suggestionContainer'>
      <Table
        ariaLabel='Ratings Table'
        columns={fixedColumns}
        itemProvider={tableItems}
        role='table'
        className='ratings-table'
        containerClassName='h-scroll-auto'
      />
      {!settings.isWebApp && (
        <div className='flex-row flex-end justify-end padding-16'>
          <Button
            text='Save rating'
            iconProps={{ iconName: 'Save' }}
            primary
            onClick={() => props.oggService.saveRating(rating)}
          />
        </div>
      )}
    </div>
  );
}

export function Rater({ streamingSession, oggService }: IActionParentProps) {
  console.debug('loading rater');
  const [streamingContent] = useStreamingSession(streamingSession);

  return (
    <Action
      presentation={{
        name: 'The Rater',
        description: <p>I'll review the work item and provide a rating for it.</p>,
        imageName: 'TheRater.png',
        onStart: () => oggService.callRaterStream(streamingSession),
      }}
      streamOutput={{
        loadingTitle: 'Ogg is rating the work item...',
        loadingSubtitle: 'Analyzing the provided data',
        render: (message) =>
          RaterContent({
            streamingContent: message,
            oggService: oggService,
          }),
        streamingContent,
        streamingSession,
      }}
    ></Action>
  );
}
